import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { dissocPath, lensPath, mergeLeft, over, pathOr, set } from 'ramda'
import { imageUrls, UserPreferences } from 'app/constants'

// PCD-10780: preferences are cached per-user, keyed by username. Usernames are emails and are
// not case sensitive in keystone, but were previously cached under whatever case the user typed
// at login, so the same user could get a different (empty) prefs bucket on every login. Normalize
// to one case everywhere a username is used as a preferences key.
export const normalizePreferencesKey = (username: string | null | undefined): string =>
  (username || '').toLowerCase()

// PCD-10780: every reader of per-user preferences must go through this helper instead of
// indexing `preferences[username]` directly, so that prefs cached under the raw (pre-normalization)
// key are still found after login. Normalized key wins when both exist.
export const getUserPrefsBucket = (
  // Callers pass the preferences slice of RootState, whose inferred type (from createSlice's
  // initialState) only has the fixed global fields (logoUrl, global, ...) and doesn't model the
  // dynamic per-username keys, so this can't be typed as PreferencesState without breaking callers.
  preferences: any,
  username: string | null | undefined,
): UserPreferenceState | undefined =>
  preferences?.[normalizePreferencesKey(username)] || preferences?.[username as string]

export enum CloudDefaults {
  Region = 'region',
  RegionLabel = 'regionDisplayName',
  Domain = 'domain', // Route 53 Domain. AWS only
  DomainLabel = 'domainLabel',
  SshKey = 'sshKey',
  SshKeyLabel = 'sshKeyLabel',
}

const initialState = {
  logoUrl: '',
  logoSrc: '',
  logoFileName: '',
  loginLogoUrl: '',
  loginLogoSrc: '',
  loginLogoFileName: '',
  global: {},
}

const {
  name: preferencesStoreKey,
  reducer: preferencesReducers,
  actions: preferencesActions,
} = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    updateLogo: (state, { payload }) => {
      return {
        ...state,
        logoUrl: payload.logoUrl,
        logoSrc: payload.logoSrc,
        logoFileName: payload.logoFileName,
      }
    },
    updateLoginLogo: (state, { payload }) => {
      return {
        ...state,
        loginLogoUrl: payload.loginLogoUrl,
        loginLogoSrc: payload.loginLogoSrc,
        loginLogoFileName: payload.loginLogoFileName,
      }
    },
    resetGlobalPrefs: (state) => {
      return {
        ...state,
        ...initialState,
      }
    },
    updateGlobalPrefs: (state, { payload }: PayloadAction<GlobalPreferenceState>) => {
      const existingPrefs = pathOr({}, ['global'], state)
      return { ...state, global: { ...existingPrefs, ...payload } }
    },
    updatePrefs: (state, { payload }: PayloadAction<PreferencesActionPayload>) => {
      const username = normalizePreferencesKey(payload.username)
      const path = Array.isArray(payload.key)
        ? [username].concat(payload.key)
        : [username, payload.key]
      return over(lensPath(path), mergeLeft(payload.prefs), state)
    },
    setPrefs: (state, { payload }: PayloadAction<PreferencesActionPayload>) => {
      const username = normalizePreferencesKey(payload.username)
      const path = Array.isArray(payload.key)
        ? [username].concat(payload.key)
        : [username, payload.key]
      return set(lensPath(path), payload.prefs, state)
    },
    removePrefs: (state, { payload }: PayloadAction<PreferencesActionPayload>) => {
      const username = normalizePreferencesKey(payload.username)
      const path = Array.isArray(payload.key)
        ? [username].concat(payload.key)
        : [username, payload.key]
      return dissocPath(path, state)
    },
    resetUserPrefs: (state) => {
      return {
        logoUrl: '',
        global: {},
        logoSrc: '',
        logoFileName: '',
        loginLogoUrl: '',
        loginLogoSrc: '',
        loginLogoFileName: '',
      }
    },
  },
})

export { preferencesActions, preferencesStoreKey }
export default preferencesReducers

export interface GlobalPreferencesActionPayload {
  key: string
  value: string | { [key: string]: any }
}
export interface PreferencesActionPayload {
  username: string
  key: string | string[]
  prefs?: { [key: string]: { [key: string]: any } } | { [key: string]: any }
}

interface UserAwsPreferences {
  [CloudDefaults.Region]: string
  [CloudDefaults.RegionLabel]: string
  [CloudDefaults.Domain]: string
  [CloudDefaults.DomainLabel]: string
  [CloudDefaults.SshKey]: string
}
interface UserAzurePreferences {
  location: string
  [CloudDefaults.Region]: string
  [CloudDefaults.SshKey]: string
}
interface CloudProviderDefaults {
  [clusterId: string]: UserAwsPreferences | UserAzurePreferences
}
interface UserDefaultsByPreference {
  [UserPreferences.FeatureFlags]: { isOnboarded: boolean }
  [UserPreferences.Dashboard]: {
    cards: string[]
    isInitialized: boolean
    hideUpgradeSuccessBanner: string[]
    hideLoadBalancerGuidedTourBanner: boolean
  }
  [UserPreferences.Cluster]: { uuid: string }
  [UserPreferences.Namespace]: { namespace: string }
  [UserPreferences.CloudProviders]: CloudProviderDefaults
}

interface GlobalPreferenceState {
  theme?: any
  customer_external_uuid?: string
  featureFlags?: GlobalFeatureFlags
}

interface GlobalFeatureFlags {
  isOnboarded: boolean
}

export interface UserPreferenceState {
  root: {
    currentTenant: string
    lastStack: string
    currentRegion: string
    rowsPerPage?: string
    lastVisitedPath?: string
    batchActionBarVariant?: string
  }
  defaults: UserDefaultsByPreference
  // Plugins (eg. kaapi's k8sPluginGlobalParams, getDataSelector's globalParams) stash
  // additional, loosely-typed keys directly on the user's prefs bucket.
  [pluginKey: string]: unknown
}
interface UserPreferencesMap {
  [key: string]: UserPreferenceState
}

export type PreferencesState = {
  logoUrl?: string
  loginLogoUrl?: string
  global?: GlobalPreferenceState
} & UserPreferencesMap
